/**
 * A minimal server-sent-events reader over `fetch` (EventSource cannot POST).
 * `parseSseChunk` is pure so the framing is testable: events end at a blank
 * line, `data:` lines concatenate, and a partial tail is carried over.
 */
export interface SseEvent { event: string; data: string }

export function parseSseChunk(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  const normalized = buffer.replace(/\r\n/g, "\n");
  const parts = normalized.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const block of parts) {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith(":")) continue;
      const idx = line.indexOf(":");
      const field = idx < 0 ? line : line.slice(0, idx);
      let value = idx < 0 ? "" : line.slice(idx + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "event") event = value;
      else if (field === "data") data.push(value);
    }
    if (data.length) events.push({ event, data: data.join("\n") });
  }
  return { events, rest };
}

export async function readSse(res: Response, onEvent: (e: SseEvent) => void): Promise<void> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseSseChunk(buffer);
    buffer = parsed.rest;
    parsed.events.forEach(onEvent);
  }
  buffer += decoder.decode();
  const tail = parseSseChunk(buffer + "\n\n");
  tail.events.forEach(onEvent);
}
